const API_BASE_URL = import.meta.env.VITE_API_URL || '4000';

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string,
    public meta?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    message: string;
    code: string;
    meta?: unknown;
  };
}

interface PaginatedApiResponse<T> {
  success: boolean;
  data: Array<T>;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  error?: {
    message: string;
    code: string;
    meta?: unknown;
  };
}

export class ApiClient {
  private baseUrl: string;
  private isServer: boolean;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
    this.isServer = typeof window === 'undefined';
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const isFormData = options.body instanceof FormData;

    const defaultHeaders: HeadersInit = {};

    // Don't set Content-Type for FormData — fetch sets it with the correct boundary
    if (!isFormData) {
      defaultHeaders['Content-Type'] = 'application/json';
    }

    // Add API key header when on server-side
    // Use process.env for server-side only variables (not exposed to client)
    if (this.isServer && process.env.API_KEY) {
      defaultHeaders['X-API-Key'] = process.env.API_KEY;
    }

    const config: RequestInit = {
      ...options,
      // Credentials only on client-side (no cookies in public SSR)
      credentials: this.isServer ? 'omit' : 'include',
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, config);

      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        // Non-JSON response (likely error HTML or plain text)
        const text = await response.text();
        throw new ApiError(text || 'Invalid response format', response.status, 'INVALID_RESPONSE');
      }

      const data: ApiResponse<T> = await response.json();

      if (!response.ok || !data.success) {
        const apiError = new ApiError(
          data.error?.message || 'An error occurred',
          response.status,
          data.error?.code,
          data.error?.meta,
        );

        // No auto-redirect on 401 for public web (unlike admin)
        // Public web doesn't use authentication

        throw apiError;
      }

      return data.data as T;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        error instanceof Error ? error.message : 'Network error',
        0,
        'NETWORK_ERROR',
      );
    }
  }

  async get<T>(endpoint: string, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'GET',
    });
  }

  async post<T>(endpoint: string, body?: unknown, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: body instanceof FormData ? body : body ? JSON.stringify(body) : undefined,
    });
  }

  async put<T>(endpoint: string, body?: unknown, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: body instanceof FormData ? body : body ? JSON.stringify(body) : undefined,
    });
  }

  async patch<T>(endpoint: string, body?: unknown, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: body instanceof FormData ? body : body ? JSON.stringify(body) : undefined,
    });
  }

  async delete<T>(endpoint: string, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'DELETE',
    });
  }

  async getPaginated<T>(
    endpoint: string,
    options?: RequestInit,
  ): Promise<{
    data: Array<T>;
    pagination: { page: number; limit: number; total: number; totalPages: number };
  }> {
    const url = `${this.baseUrl}${endpoint}`;

    const defaultHeaders: HeadersInit = {
      'Content-Type': 'application/json',
    };

    // Add API key header when on server-side
    // Use process.env for server-side only variables (not exposed to client)
    if (this.isServer && process.env.API_KEY) {
      defaultHeaders['X-API-Key'] = process.env.API_KEY;
    }

    const config: RequestInit = {
      ...options,
      credentials: this.isServer ? 'omit' : 'include',
      headers: {
        ...defaultHeaders,
        ...options?.headers,
      },
      method: 'GET',
    };

    try {
      const response = await fetch(url, config);

      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        // Non-JSON response (likely error HTML or plain text)
        const text = await response.text();
        throw new ApiError(text || 'Invalid response format', response.status, 'INVALID_RESPONSE');
      }

      const result: PaginatedApiResponse<T> = await response.json();

      if (!response.ok || !result.success) {
        const apiError = new ApiError(
          result.error?.message || 'An error occurred',
          response.status,
          result.error?.code,
          result.error?.meta,
        );

        throw apiError;
      }

      return {
        data: result.data,
        pagination: result.pagination,
      };
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        error instanceof Error ? error.message : 'Network error',
        0,
        'NETWORK_ERROR',
      );
    }
  }
}

export const apiClient = new ApiClient();
